// Session Time Hook for MUU Platform
import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';

/**
 * Hook para calcular e monitorar o tempo de sessão do usuário
 * @returns tempo de sessão em milissegundos
 */
export function useSessionTime(): number {
  const { authState } = useAuth();
  const [sessionTime, setSessionTime] = useState(0);

  useEffect(() => {
    if (!authState.isAuthenticated || !authState.sessionStartTime) {
      setSessionTime(0);
      return;
    }

    // Atualizar o tempo de sessão a cada 10 segundos para melhor performance
    const interval = setInterval(() => {
      const currentTime = Date.now();
      const elapsed = currentTime - authState.sessionStartTime!;
      setSessionTime(elapsed);
    }, 10000);

    return () => clearInterval(interval);
  }, [authState.isAuthenticated, authState.sessionStartTime]);

  return sessionTime;
}

/**
 * Hook para formatar o tempo de sessão em formato legível
 * @returns objeto com tempo formatado
 */
export function useFormattedSessionTime() {
  const sessionTime = useSessionTime();

  const formatTime = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return {
      hours,
      minutes,
      seconds,
      formatted: `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
      shortFormat: hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m ${seconds}s`,
      humanReadable: hours > 0 
        ? `${hours} hora${hours > 1 ? 's' : ''} e ${minutes} minuto${minutes !== 1 ? 's' : ''}`
        : minutes > 0
        ? `${minutes} minuto${minutes !== 1 ? 's' : ''} e ${seconds} segundo${seconds !== 1 ? 's' : ''}`
        : `${seconds} segundo${seconds !== 1 ? 's' : ''}`
    };
  };

  return {
    sessionTime,
    ...formatTime(sessionTime)
  };
}

export default useSessionTime;